/**
 * Bluedot Environment Configuration
 *
 * Centralized configuration for Bluedot Web SDK integration.
 * Reads environment variables for project ID and feature flags.
 * Resolves per-branch destinationId from branch data at runtime.
 */

// Environment variable names
const ENV_KEYS = {
  ENABLED: "GATSBY_BLUEDOT_ENABLED",
  PROJECT_ID: "GATSBY_BLUEDOT_PROJECT_ID",
  MOCK_MODE: "GATSBY_USE_BLUEDOT_MOCK",
  DEBUG_MODE: "GATSBY_BLUEDOT_DEBUG"
};

/**
 * Parse boolean environment variables
 * Handles "true", "false", "1", "0", and undefined values
 * @param {string|undefined} value - The environment variable value
 * @returns {boolean} - Parsed boolean value
 */
const parseBoolean = value => {
  if (value === undefined || value === null) {
    return false;
  }
  return value === "true" || value === "1";
};

/**
 * Get configuration value with optional default
 * @param {string} key - Environment variable key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} - Configuration value
 */
const getConfigValue = (key, defaultValue = null) => {
  if (typeof process !== "undefined" && process.env && process.env[key]) {
    return process.env[key];
  }
  // Also check window.__GATSBY__ for Gatsby client-side access
  if (
    typeof window !== "undefined" &&
    window.__GATSBY__ &&
    window.__GATSBY__[key]
  ) {
    return window.__GATSBY__[key];
  }
  return defaultValue;
};

/**
 * Get bluedotProjectId from businessAppConfiguration on the client side.
 * businessAppConfiguration is passed through Gatsby pageContext and may be
 * stored on window.__BUSINESS_APP_CONFIG__ by the layout for global access.
 * @returns {string} - The project ID or empty string
 */
const getProjectIdFromBusinessConfig = () => {
  if (typeof window !== "undefined" && window.__BUSINESS_APP_CONFIG__) {
    return window.__BUSINESS_APP_CONFIG__.bluedotProjectId || "";
  }
  return "";
};

// Internal mutable state for runtime overrides
let _runtimeProjectId = "";
let _branches = [];

/**
 * Bluedot configuration object
 */
const bluedotConfig = {
  // Feature flags
  mockMode: parseBoolean(
    process.env.GATSBY_USE_BLUEDOT_MOCK ||
      getConfigValue(ENV_KEYS.MOCK_MODE, "false")
  ),
  debugMode: parseBoolean(
    process.env.GATSBY_BLUEDOT_DEBUG ||
      getConfigValue(ENV_KEYS.DEBUG_MODE, "false")
  ),

  // SDK configuration - projectId is a getter so it can resolve dynamically
  // from env, businessAppConfiguration, or runtime override
  get projectId() {
    return (
      _runtimeProjectId ||
      process.env.GATSBY_BLUEDOT_PROJECT_ID ||
      getConfigValue(ENV_KEYS.PROJECT_ID, "") ||
      getProjectIdFromBusinessConfig() ||
      ""
    );
  },

  /**
   * Look up the Bluedot destinationId for a given branch.
   * @param {string} branchId - The Appfront branch ID
   * @returns {string} - The Bluedot destinationId, or empty string if not found
   */
  getDestinationId(branchId) {
    if (!branchId || !_branches.length) {
      return "";
    }
    const branch = _branches.find(b => b.id === branchId);
    return (branch && branch.destinationId) || "";
  },

  get enabled() {
    return (
      (this.projectId && this.projectId.length > 0) ||
      this.mockMode ||
      this.debugMode
    );
  },

  /**
   * Update config from businessAppConfiguration at runtime.
   * Call this when pageContext becomes available (e.g. in layout).
   * @param {Object} businessAppConfiguration - The business app configuration object
   */
  updateFromBusinessConfig(businessAppConfiguration) {
    if (!businessAppConfiguration) return;
    if (businessAppConfiguration.bluedotProjectId && !_runtimeProjectId) {
      _runtimeProjectId = businessAppConfiguration.bluedotProjectId;
    }
    // Also store on window for cross-module access
    if (typeof window !== "undefined") {
      window.__BUSINESS_APP_CONFIG__ = businessAppConfiguration;
    }
  },

  /**
   * Store branches so destinationId can be resolved per branch at runtime.
   * Call this when pageContext becomes available (e.g. in layout).
   * @param {Array} branches - Array of branch objects from pageContext
   */
  setBranches(branches) {
    if (Array.isArray(branches)) {
      _branches = branches;
    }
  },

  // Validation
  isValid: () => {
    return (
      bluedotConfig.enabled &&
      bluedotConfig.projectId &&
      bluedotConfig.projectId.length > 0
    );
  },

  // Debug info (safe to log)
  toDebugString: () => {
    return {
      enabled: bluedotConfig.enabled,
      projectId: bluedotConfig.projectId ? "[REDACTED]" : "[MISSING]",
      branchesWithDestinationId: _branches.filter(b => b.destinationId).length,
      mockMode: bluedotConfig.mockMode,
      debugMode: bluedotConfig.debugMode,
      isValid: bluedotConfig.isValid()
    };
  }
};

export default bluedotConfig;

export { ENV_KEYS, parseBoolean, getConfigValue };
